import React, { useState, useRef, useEffect } from 'react';
import { PiSortAscendingLight, PiSortDescendingLight } from "react-icons/pi";
import { FaFileExcel, FaFilePdf } from 'react-icons/fa';
import DatePicker from "react-datepicker";
import { CiSearch } from "react-icons/ci";
import "react-datepicker/dist/react-datepicker.css";
import { format, subDays, subMonths, startOfMonth, endOfMonth } from "date-fns";
import { jsPDF } from 'jspdf';
import * as XLSX from 'xlsx';
import "jspdf-autotable";
import axios from 'axios';
import { BaseUrl } from '../App';
import { Circles } from 'react-loader-spinner';
import { FadeLoader } from 'react-spinners';

function Staff_pay_2() {
    const [selectedOption, setSelectedOption] = useState("Today");
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [showCalendars, setShowCalendars] = useState(false);
    const calendarRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const handleSelect = (option) => {
        setSelectedOption(option);
        if (option === "Custom Range") {
            const lastMonthStart = startOfMonth(subMonths(new Date(), 1));
            const currentMonthEnd = new Date();
            setStartDate(lastMonthStart);
            setEndDate(currentMonthEnd);
            setShowCalendars(true);
        } else {
            updateDateRange(option);
            setShowCalendars(false);
        }
    };

    useEffect(() => {
        let timer;
        if (error) {
            timer = setTimeout(() => {
                setError("");
            }, 5000);
        }
        return () => clearTimeout(timer);
    }, [error]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (calendarRef.current && !calendarRef.current.contains(event.target)) {
                setShowCalendars(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (selectedOption === "Custom Range") {
            setShowCalendars(true);
        }
    }, [selectedOption]);

    const updateDateRange = (option) => {
        let start, end;
        const today = new Date();
        switch (option) {
            case "Today":
                start = end = today;
                break;
            case "Yesterday":
                start = end = subDays(today, 1);
                break;
            case "Last 7 Days":
                start = subDays(today, 7);
                end = today;
                break;
            case "Last 30 Days":
                start = subDays(today, 30);
                end = today;
                break;
            case "This Month":
                start = startOfMonth(today);
                end = endOfMonth(today);
                break;
            case "Last Month":
                const firstDayLastMonth = startOfMonth(subMonths(today, 1));
                const lastDayLastMonth = endOfMonth(subMonths(today, 1));
                start = firstDayLastMonth;
                end = lastDayLastMonth;
                break;
            case "Custom Range":
                return;
            default:
                start = end = today;
        }
        setStartDate(start);
        setEndDate(end);
    };

    const [tableData, setTableData] = useState([]);
    const [sortedTable, setSortedTable] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });

    const handleSort = (column) => {
        let direction = 'asc';
        if (sortConfig.key === column && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key: column, direction });
        const sortedData = [...sortedTable].sort((a, b) => {
            // Handle client_name specially
            const valueA = column === 'client_name'
                ? (a.client_name?.client_name != null ? String(a.client_name.client_name) : '')
                : (a[column] != null ? String(a[column]) : '');
            const valueB = column === 'client_name'
                ? (b.client_name?.client_name != null ? String(b.client_name.client_name) : '')
                : (b[column] != null ? String(b[column]) : '');
            if (valueA < valueB) return direction === 'asc' ? -1 : 1;
            if (valueA > valueB) return direction === 'asc' ? 1 : -1;
            return 0;
        });
        setSortedTable(sortedData);
    };

    const getSortIcon = (column) => {
        if (sortConfig.key !== column) {
            return <PiSortAscendingLight className="text-gray-500" />;
        }
        return sortConfig.direction === 'asc' ? <PiSortAscendingLight /> : <PiSortDescendingLight />;
    };

    useEffect(() => {
        setSortedTable(Array.isArray(tableData) ? tableData : []);
    }, [tableData]);

    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    const filteredData = sortedTable.filter((row) => {
        // Safely convert values to strings and handle null/undefined
        const clientName = row.client_name?.client_name != null ? String(row.client_name.client_name).toLowerCase() : '';
        const staffName = row.staff_name != null ? String(row.staff_name).toLowerCase() : '';
        const paymentMethod = row.payment_method != null ? String(row.payment_method).toLowerCase() : '';
        const paymentDate = row.payment_date != null ? String(row.payment_date).toLowerCase() : '';
        const amtPayable = row.amt_payable != null ? String(row.amt_payable).toLowerCase() : '';
        const advancePaid = row.advance_paid != null ? String(row.advance_paid).toLowerCase() : '';
        const netTotal = row.net_total != null ? String(row.net_total).toLowerCase() : '';

        const query = searchQuery.toLowerCase();
        return (
            clientName.includes(query) ||
            staffName.includes(query) ||
            paymentMethod.includes(query) ||
            paymentDate.includes(query) ||
            amtPayable.includes(query) ||
            advancePaid.includes(query) ||
            netTotal.includes(query)
        );
    });

    const startIndex = (currentPage - 1) * entriesPerPage;
    const paginatedData = entriesPerPage === "All"
        ? filteredData
        : filteredData.slice(startIndex, startIndex + parseInt(entriesPerPage));

    const totalPages = Math.ceil(filteredData.length / (entriesPerPage === "All" ? 1 : entriesPerPage));

    const handleSearch = (e) => setSearchQuery(e.target.value);

    const handleEntriesChange = (e) => {
        setEntriesPerPage(e.target.value);
        setCurrentPage(1);
    };

    const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
    const handleNextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));
    const handlePageChange = (page) => setCurrentPage(page);

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    const handleExcelExport = () => {
        const columns = [
            'S.No',
            'Order_id',
            'Client',
            'Staff Name',
            'Payment Method',
            'Payment Date',
            'Amount',
            'Advance Paid',
            'Net Total'
        ];
        const rows = sortedTable.map((row, index) => [
            index + 1,
            row.order_id || 'N/A',
            row.client_name?.client_name || 'N/A',
            row.staff_name || 'N/A',
            row.payment_method || 'N/A',
            row.payment_date || 'N/A',
            row.amt_payable || 'N/A',
            row.advance_paid || 'N/A',
            row.net_total || 'N/A',
        ]);

        const ws = XLSX.utils.aoa_to_sheet([columns, ...rows]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Staff Payments');
        XLSX.writeFile(wb, 'staff-payments.xlsx');
    };

    const handlePdfExport = () => {
        const doc = new jsPDF();
        const columns = [
            'S.No',
            'Order_id',
            'Client',
            'Staff Name',
            'Payment Method',
            'Payment Date',
            'Amount',
            'Advance Paid',
            'Net Total'
        ];
        const rows = sortedTable.map((row, index) => [
            index + 1,
            row.order_id || 'N/A',
            row.client_name?.client_name || 'N/A',
            row.staff_name || 'N/A',
            row.payment_method || 'N/A',
            row.payment_date || 'N/A',
            row.amt_payable || 'N/A',
            row.advance_paid || 'N/A',
            row.net_total || 'N/A',
        ]);
        doc.autoTable({
            head: [columns],
            body: rows,
            startY: 20,
            theme: 'grid',
            margin: { horizontal: 10 },
            headStyles: {
                fillColor: [0, 123, 255],
                textColor: 255,
            },
        });
        doc.save('staff-payments.pdf');
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await axios.post(`${BaseUrl}/backend/api/payment_by_user_type.php`, {
                action: "get_payment_by_user_type",
                api_key: "1",
                user_type: 'staff',
                limit: 10,
                offset: 0
            });

            console.log("API Response:", response.data);

            if (response.data.status === "true" && response.data.statuscode === 200) {
                const newData = Array.isArray(response.data.response) ? response.data.response : [];
                // Normalize data to flatten client_name
                const normalizedData = newData.map(row => ({
                    ...row,
                    order_id: row.order_id != null ? String(row.order_id) : 'N/A',
                    client_name: row.client_name?.client_name != null ? String(row.client_name.client_name) : 'N/A',
                    client_email: row.client_name?.client_email != null ? String(row.client_name.client_email) : 'N/A',
                    staff_name: row.staff_name != null ? String(row.staff_name) : 'N/A',
                    payment_method: row.payment_method != null ? String(row.payment_method) : 'N/A',
                    payment_date: row.payment_date != null ? String(row.payment_date) : 'N/A',
                    amt_payable: row.amt_payable != null ? String(row.amt_payable) : 'N/A',
                    advance_paid: row.advance_paid != null ? String(row.advance_paid) : 'N/A',
                    net_total: row.net_total != null ? String(row.net_total) : 'N/A',
                }));
                setTableData(normalizedData);
                setSortedTable(normalizedData);
            } else {
                console.error('No payments available or error in response');
                setTableData([]);
                setSortedTable([]);
            }
        } catch (error) {
            console.error('Error fetching staff payment data:', error);
            setTableData([]);
            setSortedTable([]);
            setError("Failed to fetch staff payment data. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    return (
        <div className="relative min-h-screen">
            {loading && (
                <div className="fixed inset-0 flex items-center justify-center z-50">
                    <div className="flex flex-col items-center p-3 bg-black border border-white rounded-2xl">
                        <FadeLoader
                            height={15}
                            width={5}
                            color="#ffffff"
                            ariaLabel="loading"
                        />
                        <h1 className="mt-1 text-lg text-white">Please wait...</h1>
                    </div>
                </div>
            )}

            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4 mx-4 xl:mx-60">
                    {error}
                </div>
            )}

            <div className="min-h-screen bg-white p-4 xl:px-60">
                <div className="text-2xl font-bold p-4">
                    <span>Staff Payments Details</span>
                </div>

                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center w-full">
                    <div className="flex flex-col sm:flex-row items-center">
                        <div className="relative w-full sm:w-auto">
                            <input
                                type="text"
                                placeholder="Search Staff..."
                                value={searchQuery}
                                onChange={handleSearch}
                                className="p-3 w-full sm:w-80 md:w-96 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300 pl-10"
                            />
                            <CiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                        </div>
                        <div className="bg-gray-100 p-1 rounded-lg shadow-md mb-6 w-full sm:w-auto ml-0 sm:ml-5 mt-4 sm:mt-0">
                            <div className="flex flex-col sm:flex-row gap-4">
                                <div>
                                    <select
                                        className="p-3 w-full sm:w-32 mt-4 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        value={selectedOption}
                                        onChange={(e) => handleSelect(e.target.value)}
                                    >
                                        <option value="Today">Today</option>
                                        <option value="Yesterday">Yesterday</option>
                                        <option value="Last 7 Days">Last 7 Days</option>
                                        <option value="Last 30 Days">Last 30 Days</option>
                                        <option value="This Month">This Month</option>
                                        <option value="Last Month">Last Month</option>
                                        <option value="Custom Range">Custom Range</option>
                                    </select>
                                </div>
                                {selectedOption === "Custom Range" ? (
                                    <div className="relative w-full sm:w-auto" ref={calendarRef}>
                                        <label className="text-sm text-gray-600">Select Date Range</label>
                                        <div className="flex gap-4 flex-col sm:flex-row">
                                            {showCalendars && (
                                                <>
                                                    <div className="sm:hidden flex flex-col space-y-4">
                                                        <div className="bg-white shadow-lg rounded-md border z-10 w-full">
                                                            <DatePicker
                                                                selected={startDate}
                                                                onChange={(date) => setStartDate(date)}
                                                                selectsStart
                                                                startDate={startDate}
                                                                endDate={endDate}
                                                                className="px-4 py-2 w-full"
                                                                inline
                                                            />
                                                        </div>
                                                        <div className="bg-white shadow-lg rounded-md border z-10 w-full">
                                                            <DatePicker
                                                                selected={endDate}
                                                                onChange={(date) => setEndDate(date)}
                                                                selectsEnd
                                                                startDate={startDate}
                                                                endDate={endDate}
                                                                className="px-4 py-2 w-full"
                                                                inline
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="hidden sm:block">
                                                        <div className="absolute bg-white shadow-lg rounded-md border z-10 left-0">
                                                            <DatePicker
                                                                selected={startDate}
                                                                onChange={(date) => setStartDate(date)}
                                                                selectsStart
                                                                startDate={startDate}
                                                                endDate={endDate}
                                                                className="px-4 py-2 w-full"
                                                                inline
                                                            />
                                                        </div>
                                                        <div className="absolute bg-white shadow-lg rounded-md border z-10 left-[245px]">
                                                            <DatePicker
                                                                selected={endDate}
                                                                onChange={(date) => setEndDate(date)}
                                                                selectsEnd
                                                                startDate={startDate}
                                                                endDate={endDate}
                                                                className="px-4 py-2 w-full"
                                                                inline
                                                            />
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex gap-4 flex-col sm:flex-row w-full">
                                        <div>
                                            <label className="block text-sm text-gray-600">Start Date</label>
                                            <input
                                                type="text"
                                                value={format(startDate, "yyyy-MM-dd")}
                                                disabled
                                                className="px-4 py-2 w-full border rounded-md bg-gray-200"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm text-gray-600">End Date</label>
                                            <input
                                                type="text"
                                                value={format(endDate, "yyyy-MM-dd")}
                                                disabled
                                                className="px-4 py-2 w-full border rounded-md bg-gray-200"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap justify-between items-center gap-4 mt-4 sm:mt-0">
                        <div className="flex gap-4 items-center">
                            <button
                                onClick={handleExcelExport}
                                className="bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold text-sm sm:text-base py-2 px-4 rounded-full shadow-lg hover:shadow-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-300 flex items-center gap-2"
                            >
                                <FaFileExcel className="text-lg" />
                                Excel
                            </button>
                            <button
                                onClick={handlePdfExport}
                                className="bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold text-sm sm:text-base py-2 px-4 rounded-full shadow-lg hover:shadow-xl hover:from-red-600 hover:to-red-700 transition-all duration-300 flex items-center gap-2"
                            >
                                <FaFilePdf className="text-lg" />
                                PDF
                            </button>
                        </div>
                        <div className="flex items-center gap-1 sm:mt-0 mt-2">
                            <span>Show</span>
                            <select
                                value={entriesPerPage}
                                onChange={handleEntriesChange}
                                className="p-2 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value="All">All</option>
                            </select>
                            <span>entries</span>
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto mt-6">
                    <table className="min-w-full table-auto bg-white border border-gray-300 shadow-md rounded-lg hidden sm:table">
                        <thead className="bg-blue-100 text-left">
                            <tr>
                                <th className="px-4 py-2 border-b cursor-pointer">#</th>
                                <th className="px-4 py-2 border-b cursor-pointer" onClick={() => handleSort('order_id')}>
                                    <div className="flex items-center gap-2">
                                        Order ID {getSortIcon('order_id')}
                                    </div>
                                </th>
                                <th className="px-4 py-2 border-b cursor-pointer" onClick={() => handleSort('client_name')}>
                                    <div className="flex items-center gap-2">
                                        Client {getSortIcon('client_name')}
                                    </div>
                                </th>
                                <th className="px-4 py-2 border-b cursor-pointer" onClick={() => handleSort('staff_name')}>
                                    <div className="flex items-center gap-2">
                                        Staff Name {getSortIcon('staff_name')}
                                    </div>
                                </th>
                                <th className="px-4 py-2 border-b cursor-pointer" onClick={() => handleSort('payment_method')}>
                                    <div className="flex items-center gap-2">
                                        Payment Method {getSortIcon('payment_method')}
                                    </div>
                                </th>
                                <th className="px-4 py-2 border-b cursor-pointer" onClick={() => handleSort('payment_date')}>
                                    <div className="flex items-center gap-2">
                                        Payment Date {getSortIcon('payment_date')}
                                    </div>
                                </th>
                                <th className="px-4 py-2 border-b cursor-pointer" onClick={() => handleSort('amt_payable')}>
                                    <div className="flex items-center gap-2">
                                        Amount {getSortIcon('amt_payable')}
                                    </div>
                                </th>
                                <th className="px-4 py-2 border-b cursor-pointer" onClick={() => handleSort('advance_paid')}>
                                    <div className="flex items-center gap-2">
                                        Advance Paid {getSortIcon('advance_paid')}
                                    </div>
                                </th>
                                <th className="px-4 py-2 border-b cursor-pointer" onClick={() => handleSort('net_total')}>
                                    <div className="flex items-center gap-2">
                                        Net Total {getSortIcon('net_total')}
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedData.length > 0 ? (
                                paginatedData.map((row, index) => (
                                    <tr key={index} className="hover:bg-gray-50">
                                        <td className="px-4 py-2 border-b">{startIndex + index + 1}</td>
                                        <td className="px-4 py-2 border-b">{row.order_id}</td>
                                        <td className="px-4 py-2 border-b">{row.client_name}</td>
                                        <td className="px-4 py-2 border-b">{row.staff_name}</td>
                                        <td className="px-4 py-2 border-b">{row.payment_method}</td>
                                        <td className="px-4 py-2 border-b">{row.payment_date}</td>
                                        <td className="px-4 py-2 border-b">{row.amt_payable}</td>
                                        <td className="px-4 py-2 border-b">{row.advance_paid}</td>
                                        <td className="px-4 py-2 border-b">{row.net_total}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="9" className="text-center py-4">No data available</td>
                                </tr>
                            )}
                        </tbody>
                    </table>

                    <div className="sm:hidden space-y-4">
                        {paginatedData.length > 0 ? (
                            paginatedData.map((row, index) => (
                                <div
                                    key={index}
                                    className="bg-white border border-gray-300 shadow-md rounded-lg p-4"
                                >
                                    <div className="mb-2 pb-2 border-b border-gray-200">
                                        <span className="font-bold text-lg text-blue-600">#{startIndex + index + 1}</span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div className="mb-2">
                                            <span className="font-semibold text-gray-700 block">Order ID:</span>
                                            <span className="text-gray-900">{row.order_id}</span>
                                        </div>
                                        <div className="mb-2">
                                            <span className="font-semibold text-gray-700 block">Client:</span>
                                            <span className="text-gray-900">{row.client_name}</span>
                                        </div>
                                        <div className="mb-2">
                                            <span className="font-semibold text-gray-700 block">Staff Name:</span>
                                            <span className="text-gray-900">{row.staff_name}</span>
                                        </div>
                                        <div className="mb-2">
                                            <span className="font-semibold text-gray-700 block">Payment Method:</span>
                                            <span className="text-gray-900">{row.payment_method}</span>
                                        </div>
                                        <div className="mb-2">
                                            <span className="font-semibold text-gray-700 block">Payment Date:</span>
                                            <span className="text-gray-900">{row.payment_date}</span>
                                        </div>
                                        <div className="mb-2">
                                            <span className="font-semibold text-gray-700 block">Amount:</span>
                                            <span className="text-gray-900">{row.amt_payable}</span>
                                        </div>
                                        <div className="mb-2">
                                            <span className="font-semibold text-gray-700 block">Advance Paid:</span>
                                            <span className="text-gray-900">{row.advance_paid}</span>
                                        </div>
                                    </div>
                                    <div className="mt-3 pt-2 border-t border-gray-200">
                                        <span className="font-bold text-gray-800 block">Net Total:</span>
                                        <span className="text-xl font-semibold text-green-600">{row.net_total}</span>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-8 bg-white border border-gray-300 rounded-lg shadow-md">
                                <p className="text-gray-500 font-medium">No data available</p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row justify-between items-center mt-6">
                    <div className="text-sm text-gray-600 mb-4 sm:mb-0">
                        {filteredData.length > 0 ? (
                            <span>
                                Showing {startIndex + 1} to {Math.min(startIndex + parseInt(entriesPerPage === "All" ? filteredData.length : entriesPerPage), filteredData.length)} of {filteredData.length} entries
                            </span>
                        ) : (
                            <span>Showing 0 to 0 of 0 entries</span>
                        )}
                    </div>
                    <div className="flex flex-col sm:flex-row items-center gap-4">
                        {entriesPerPage !== "All" && totalPages > 0 && (
                            <div className="flex flex-wrap justify-center items-center mb-4 sm:mb-0">
                                <button
                                    onClick={handlePrevPage}
                                    disabled={currentPage === 1}
                                    className="px-3 py-1 bg-gray-300 rounded-l hover:bg-gray-400 disabled:opacity-50"
                                >
                                    Prev
                                </button>
                                {[...Array(Math.min(totalPages, 5)).keys()].map((page) => {
                                    const pageNumber = page + 1;
                                    const shouldShow =
                                        pageNumber === 1 ||
                                        pageNumber === totalPages ||
                                        Math.abs(pageNumber - currentPage) <= 1;
                                    return shouldShow ? (
                                        <button
                                            key={pageNumber}
                                            onClick={() => handlePageChange(pageNumber)}
                                            className={`px-3 py-1 border ${
                                                currentPage === pageNumber
                                                    ? "bg-blue-500 text-white"
                                                    : "bg-gray-200 hover:bg-gray-300"
                                            }`}
                                        >
                                            {pageNumber}
                                        </button>
                                    ) : pageNumber === 2 || pageNumber === totalPages - 1 ? (
                                        <span key={pageNumber} className="px-2 py-1">...</span>
                                    ) : null;
                                })}
                                <button
                                    onClick={handleNextPage}
                                    disabled={currentPage === totalPages || totalPages === 0}
                                    className="px-3 py-1 bg-gray-300 rounded-r hover:bg-gray-400 disabled:opacity-50"
                                >
                                    Next
                                </button>
                            </div>
                        )}
                        <button
                            onClick={toggleSidebar}
                            className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold text-base py-2 px-6 rounded-full shadow-lg hover:shadow-xl hover:from-blue-600 hover:to-indigo-700 transition-all duration-300 flex items-center gap-2"
                        >
                            Submit
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Staff_pay_2;